import React, { useCallback, useEffect, useRef, useState } from 'react'
import styles from '@shared/styles/components/Modal.module.css'
import { ModalHeader } from '@shared/designSystem/ModalHeader'

interface ModalProps {
    children: React.ReactNode
    isOpen: boolean
    onClose: () => void
    title?: string
    /** Optional line of context rendered under the title, inside the header block. */
    subtitle?: string
    width?: string
    height?: string
    maxWidth?: string
    maxHeight?: string
    className?: string
    /**
     * When false the modal can only be left through its own content (e.g. an
     * "Entendido" button): no close icon, and Esc / backdrop clicks are ignored.
     */
    dismissible?: boolean
}

export const Modal: React.FC<ModalProps> = ({
    children,
    isOpen,
    onClose,
    title = '',
    subtitle,
    width = '80%',
    height = 'auto',
    maxWidth = '1200px',
    maxHeight = '90vh',
    className,
    dismissible = true
}) => {
    const closeAnimationMs = 180
    const [isVisible, setIsVisible] = useState(isOpen)
    const [isClosing, setIsClosing] = useState(false)
    const lastOpenChildrenRef = useRef<React.ReactNode>(children)
    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (dismissible && e.target === e.currentTarget) {
            handleClose()
        }
    }

    const handleClose = useCallback(() => {
        onClose()
    }, [onClose])

    useEffect(() => {
        if (!isOpen || !dismissible) {
            return
        }
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                handleClose()
            }
        }

        window.addEventListener("keydown", handleKeyDown)
        return () => window.removeEventListener("keydown", handleKeyDown)
    }, [dismissible, handleClose, isOpen])

    useEffect(() => {
        if (isOpen) {
            setIsVisible(true)
            setIsClosing(false)
        } else if (isVisible) {
            setIsClosing(true)
            const timeout = window.setTimeout(() => {
                setIsClosing(false)
                setIsVisible(false)
            }, closeAnimationMs)
            return () => window.clearTimeout(timeout)
        }
    }, [closeAnimationMs, isOpen, isVisible])

    if (!isVisible) return null
    if (isOpen) {
        lastOpenChildrenRef.current = children
    }
    const renderChildren = isOpen ? children : lastOpenChildrenRef.current

    return (
        <div
            className={[
                styles['modal-backdrop'],
                isClosing ? styles['modal-backdrop-closing'] : styles['modal-backdrop-open'],
                'DSModalBackdrop'
            ].join(' ')}
            onClick={handleBackdropClick}
        >
            <div 
                className={[
                    styles['modal-container'],
                    isClosing ? styles['modal-container-closing'] : styles['modal-container-open'],
                    'DSModal',
                    className
                ].filter(Boolean).join(' ')}
                role="dialog"
                aria-modal="true"
                aria-label={title || undefined}
                style={{
                    width: width,
                    height: height,
                    maxWidth: maxWidth,
                    maxHeight: maxHeight
                }}
            >
                <div className={styles['modal-header']}>
                    <ModalHeader title={title} subtitle={subtitle} onClose={dismissible ? handleClose : undefined} />
                </div>
                <div className={styles['modal-content']}>
                    {renderChildren}
                </div>
            </div>
        </div>
    )
}
